import React from "react";
import { Button, Stack } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CompetitionVoteButton from "./CompetitionVoteButton";
import { shouldShowBuyVotes } from "../../../utils/competitionVotePurchase";

export const BUY_VOTES_COLORS = {
  enabled: {
    text: "#8fd99a",
    border: "rgba(129, 199, 132, 0.45)",
    bg: "rgba(46, 125, 50, 0.14)",
    hoverBg: "rgba(46, 125, 50, 0.24)",
    hoverBorder: "#81c784",
    solidBg: "#2e7d32",
    solidHoverBg: "#256628",
    solidText: "#fff",
  },
  disabled: {
    text: "rgba(255,255,255,0.45)",
    border: "rgba(255,255,255,0.12)",
    bg: "rgba(255,255,255,0.04)",
    solidBg: "#5c4033",
    solidHoverBg: "#4a3329",
    solidText: "#fff",
  },
};

function getBuyVotesSx(variant, enabled, showLabel, lightSurface = false) {
  const c = enabled ? BUY_VOTES_COLORS.enabled : BUY_VOTES_COLORS.disabled;
  const disabledOnLight = {
    text: "#666666",
    border: "#dddddd",
    bg: "#f0f0f0",
    hoverBg: "#e8e8e8",
    hoverBorder: "#cccccc",
  };
  const palette =
    !enabled && lightSurface && variant === "filter"
      ? { ...c, ...disabledOnLight }
      : c;

  if (variant === "toolbar") {
    return {
      textTransform: "none",
      fontWeight: 700,
      bgcolor: c.solidBg,
      color: c.solidText,
      borderRadius: "8px",
      px: { xs: 1.2, sm: 2 },
      boxShadow: "none",
      "&:hover": {
        bgcolor: c.solidHoverBg,
        boxShadow: "none",
      },
    };
  }

  const filterStyle = variant === "filter";

  return {
    textTransform: "none",
    fontWeight: filterStyle ? 600 : 700,
    fontSize: filterStyle ? 13 : 11,
    minWidth: filterStyle ? 140 : !showLabel ? 36 : undefined,
    height: filterStyle ? 40 : undefined,
    px: filterStyle ? 1.75 : showLabel ? 1.1 : 0.75,
    py: filterStyle ? undefined : 0.35,
    borderRadius: filterStyle ? 2 : 999,
    color: palette.text,
    border: `1px solid ${palette.border}`,
    bgcolor: palette.bg,
    lineHeight: 1.2,
    "&:hover": enabled
      ? {
          bgcolor: palette.hoverBg,
          borderColor: palette.hoverBorder,
        }
      : {
          bgcolor: lightSurface ? palette.hoverBg : "rgba(255,255,255,0.06)",
        },
  };
}

/**
 * Opens the vote purchase modal.
 * variant: toolbar (top bar), filter (videos header), compact (video row).
 */
export default function CompetitionBuyVotesTrigger({
  onClick,
  enabled = true,
  variant = "filter",
  compact,
  showLabel = true,
  lightSurface = false,
}) {
  const resolvedVariant = compact === true ? "compact" : compact === false ? "filter" : variant;
  const label = enabled
    ? "Buy votes"
    : resolvedVariant === "toolbar"
      ? "Payments suspended"
      : "Payments off";

  return (
    <Button
      size="small"
      variant={resolvedVariant === "toolbar" ? "contained" : "text"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(e);
      }}
      startIcon={
        <ShoppingCartOutlinedIcon
          sx={{ fontSize: resolvedVariant === "compact" ? 16 : 18 }}
        />
      }
      sx={getBuyVotesSx(resolvedVariant, enabled, showLabel, lightSurface)}
    >
      {showLabel ? label : null}
    </Button>
  );
}

export function CompetitionVoteActions({
  videoId,
  eventId,
  voteCount,
  votingEnabled,
  onVoted,
  votePurchase,
  onBuyVotes,
  remainingVotes = 0,
  maxFreeVotesPerDay = 1,
  dailyVotesUsed = 0,
  lightSurface = false,
}) {
  const showBuy = shouldShowBuyVotes({
    votePurchase,
    remainingVotes,
    maxFreeVotesPerDay,
    dailyVotesUsed,
  });

  return (
    <Stack direction="column" alignItems="flex-end" gap={0.75} flexShrink={0}>
      <CompetitionVoteButton
        videoId={videoId}
        count={voteCount}
        disabled={!votingEnabled}
        onVoted={onVoted}
        compact
        lightSurface={lightSurface}
      />
      {showBuy ? (
        <CompetitionBuyVotesTrigger
          onClick={onBuyVotes}
          enabled={Boolean(votePurchase?.enabled)}
          variant="compact"
          lightSurface={lightSurface}
        />
      ) : null}
    </Stack>
  );
}
